<template>
    <div class="p-4">
        <Card class="w-full shadow-2 border-round-lg p-5">
            <template #title>
                <h3 class="m-0 text-lg font-semibold">{{ isEdit ? 'Edit Exam Result' : 'Add Exam Result' }}</h3>
            </template>

            <template #content>
                <form @submit.prevent="submitForm" class="p-fluid">
                    <div class="grid">
                        <div class="col-12 md:col-3">
                            <div class="text-left p-3 border-round-sm font-bold relative mb-4">
                                <label for="subject" class="font-medium mb-2 block">Subject</label>
                                <InputText id="subject" v-model.trim="form.subject" placeholder="Mathematics"
                                    class="w-full" />
                                <small v-if="errors.subject" class="error-text">{{ errors.subject }}</small>
                            </div>
                        </div>

                        <div class="col-12 md:col-3">
                            <div class="text-left p-3 border-round-sm font-bold relative mb-4">
                                <label for="examName" class="font-medium mb-2 block">Exam Name</label>
                                <InputText id="examName" v-model.trim="form.examName" placeholder="Term 1 Final"
                                    class="w-full" />
                                <small v-if="errors.examName" class="error-text">{{ errors.examName }}</small>
                            </div>
                        </div>

                        <div class="col-12 md:col-3">
                            <div class="text-left p-3 border-round-sm font-bold relative mb-4">
                                <label for="date" class="font-medium mb-2 block">Date</label>
                                <DatePicker id="date" v-model="form.date" hourFormat="12" showIcon iconDisplay="input"
                                    variant="filled" class="w-full" />
                                <small v-if="errors.date" class="error-text">{{ errors.date }}</small>
                            </div>
                        </div>

                        <div class="col-12 md:col-3">
                            <div class="text-left p-3 border-round-sm font-bold relative mb-4">
                                <label for="marks" class="font-medium mb-2 block">Marks</label>
                                <InputText id="marks" v-model.trim="form.marks" placeholder="85" class="w-full" />
                                <small v-if="errors.marks" class="error-text">{{ errors.marks }}</small>
                            </div>
                        </div>
                    </div>

                    <div class="grid">
                        <div class="col-12">
                            <div class="text-left p-3">
                                <label for="feedback" class="font-medium mb-2 block">Feedback</label>
                                <Editor id="feedback" v-model="form.feedback" editorStyle="height: 160px"
                                    class="border-round-sm" placeholder="Enter feedback..." />
                            </div>
                        </div>
                    </div>

                    <div class="flex justify-content-end mt-3 gap-2">
                        <Button :label="isEdit ? 'Update Result' : 'Save Result'" icon="pi pi-check"
                            :class="[isInstitute ? 'yellow-button-small' : 'teal-button-small']" :loading="submitting" type="submit" />
                    </div>
                </form>
            </template>
        </Card>
    </div>
</template>

<script setup>
import { notifyError } from "@/utils/notification";
import { Button, Card, DatePicker, InputText } from "primevue";
import Editor from "primevue/editor";
import { ref, defineEmits, defineProps, watch, computed, nextTick } from "vue";
import { useAuthStore } from "@/stores/authStore";

const props = defineProps({
    result: {
        type: Object,
        default: null
    }
});

const emit = defineEmits(["save-result"]);
const authStore = useAuthStore();
const isInstitute = computed(() => authStore.user?.role === 'institute');
const submitting = ref(false);
const errors = ref({});
const isEdit = computed(() => !!props.result);

const form = ref({
    subject: "",
    examName: "",
    date: null,
    marks: "",
    feedback: "",
});

const validateForm = () => {
    errors.value = {};
    if (!form.value.subject) errors.value.subject = "Subject is required";
    if (!form.value.examName) errors.value.examName = "Exam Name is required";
    if (!form.value.date) errors.value.date = "Date is required";
    if (!form.value.marks) errors.value.marks = "Marks are required";
    return Object.keys(errors.value).length === 0;
};

const submitForm = async () => {
    if (!validateForm()) {
        notifyError("Error", "Please fill all required fields.");
        return;
    }

    try {
        submitting.value = true;
        const payload = { ...form.value };
        if (isEdit.value) {
            payload._id = props.result._id;
        }
        emit('save-result', payload);
    } catch (err) {
        notifyError("Error", "Failed to save result.");
    } finally {
        submitting.value = false;
    }
};

// Watch for changes in props.result to populate or clear the form
watch(
    () => props.result,
    async (data) => {
        await nextTick();
        if (data) {
            // Populate for Edit
            form.value = {
                subject: data.subject || "",
                examName: data.examName || "",
                date: data.date ? new Date(data.date) : null,
                marks: data.marks || "",
                feedback: data.feedback || ""
            };
        } else {
            // Reset for Create
            form.value = {
                subject: "",
                examName: "",
                date: null,
                marks: "",
                feedback: ""
            };
        }
    },
    { immediate: true }
);
</script>

<style scoped>
.teal-button-small {
    padding: 8px 16px;
    height: 30px;
    font-size: 0.75rem;
}

:deep(.p-datepicker-input) {
    min-width: 0;
    width: 100%;
}

:deep(.p-datepicker) {
    width: 100%;
    max-width: 100%;
}

.error-text {
    color: #dc2626;
    font-size: 0.85rem;
    position: absolute;
    top: 100%;
    left: 1rem;
}
</style>